import { useEffect, useState, useRef, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import Peer, { DataConnection } from 'peerjs';
import './Components.css';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';
type TransferState = 'waiting' | 'request-received' | 'transferring' | 'completed' | 'failed';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

interface TransferRequest {
    fileCount: number;
    totalSize: number;
    files: FileInfo[];
}

interface ReceivingFileState {
    id: string;
    name: string;
    size: number;
    receivedBytes: number;
    chunks: ArrayBuffer[];
    url?: string;
    progress: number;
}

interface ReceiverProps {
    goBack: () => void;
}

const Receiver = ({ goBack }: ReceiverProps) => {
    const [peerId, setPeerId] = useState('');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [transferState, setTransferState] = useState<TransferState>('waiting');
    const [transferRequest, setTransferRequest] = useState<TransferRequest | null>(null);
    const [receivingFiles, setReceivingFiles] = useState<Map<string, ReceivingFileState>>(new Map());
    const [requestTimeout, setRequestTimeout] = useState<NodeJS.Timeout | null>(null);
    
    const peerRef = useRef<Peer | null>(null);
    const currentConnectionRef = useRef<DataConnection | null>(null);

    const updateReceivingFile = useCallback((id: string, updates: Partial<ReceivingFileState>) => {
        setReceivingFiles(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(id);
            if (existing) {
                newMap.set(id, { ...existing, ...updates });
            }
            return newMap;
        });
    }, []);

    const handleChunk = useCallback((payload: { transferId: string; chunk: ArrayBuffer }) => {
        setReceivingFiles(prev => {
            const newMap = new Map(prev);
            const fileState = newMap.get(payload.transferId);

            if (fileState) {
                fileState.chunks.push(payload.chunk);
                const receivedBytes = fileState.receivedBytes + payload.chunk.byteLength;
                const progress = Math.round((receivedBytes / fileState.size) * 100);
                
                const updatedFileState = { ...fileState, receivedBytes, progress };

                if (receivedBytes >= fileState.size) {
                    const blob = new Blob(fileState.chunks, { type: 'application/octet-stream' });
                    updatedFileState.url = URL.createObjectURL(blob);
                    if (currentConnectionRef.current) {
                        currentConnectionRef.current.send({ type: 'chunk-ack', payload: { transferId: payload.transferId } });
                    }
                }
                
                newMap.set(payload.transferId, updatedFileState);

                if (Array.from(newMap.values()).every(f => f.url)) {
                    setTransferState('completed');
                }
            }
            return newMap;
        });
    }, []);

    useEffect(() => {
        const peer = new Peer({ host: 'local-transfer-server.onrender.com', secure: true });
        peerRef.current = peer;

        const handleData = (data: any, conn: DataConnection) => {
            console.log('Receiver got data:', data);
            if (data.type === 'transfer-request') {
                handleTransferRequest(data.payload, conn);
            } else if (data.type === 'metadata') {
                handleMetadata(data.payload, conn);
            } else if (data.type === 'chunk') {
                handleChunk(data.payload);
            }
        };

        peer.on('open', (id) => {
            console.log('Receiver peer opened with ID:', id);
            setPeerId(id);
        });
        peer.on('error', (err) => console.error('Receiver peer error:', err));
        peer.on('connection', (conn) => {
            console.log('Incoming connection received from:', conn.peer);
            setConnectionStatus('connecting');
            currentConnectionRef.current = conn;
            conn.on('open', () => {
                console.log('Connection opened successfully.');
                setConnectionStatus('connected');
            });
            conn.on('data', (data: any) => handleData(data, conn));
            conn.on('close', () => {
                console.log('Connection closed.');
                setConnectionStatus('disconnected');
            });
            conn.on('error', (err) => {
                console.error('Connection error:', err);
                setConnectionStatus('disconnected');
            });
        });

        return () => peer.destroy();
    }, []);

    const handleTransferRequest = (request: TransferRequest, conn: DataConnection) => {
        console.log('Handling transfer request:', request);
        setTransferRequest(request);
        setTransferState('request-received');
        if (requestTimeout) clearTimeout(requestTimeout);
        const timeout = setTimeout(() => declineTransfer(), 30000);
        setRequestTimeout(timeout);
    };

    const handleMetadata = (payload: { transferId: string; name: string; size: number }, conn: DataConnection) => {
        const { transferId, name, size } = payload;
        console.log(`Received metadata for ${name} (${transferId})`);
        setReceivingFiles(prev => new Map(prev).set(transferId, {
            id: transferId,
            name,
            size,
            receivedBytes: 0,
            chunks: [],
            progress: 0,
        }));
        console.log(`Sending metadata-ack for ${transferId}`);
        conn.send({ type: 'metadata-ack', payload: { transferId } });
    };

    const acceptTransfer = () => {
        if (!currentConnectionRef.current) return;
        console.log('Accepting transfer...');
        // Send the message first to avoid race condition with re-render
        currentConnectionRef.current.send({ type: 'transfer-accept' });
        setTransferState('transferring');
        if (requestTimeout) clearTimeout(requestTimeout);
    };

    const declineTransfer = () => {
        if (!currentConnectionRef.current) return;
        setTransferState('waiting');
        setTransferRequest(null);
        currentConnectionRef.current.send({ type: 'transfer-decline' });
        if (requestTimeout) clearTimeout(requestTimeout);
    };

    const downloadAllFiles = () => {
        receivingFiles.forEach((file) => {
            if (file.url) {
                const a = document.createElement('a');
                a.href = file.url;
                a.download = file.name;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(file.url);
            }
        });
        setReceivingFiles(new Map());
        setTransferState('waiting');
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    return (
        <div className="container">
            <button className="back-button" onClick={goBack}><i className="fas fa-arrow-left"></i> Back</button>
            <h2>Receiver</h2>
            {transferState === 'request-received' && transferRequest && (
                <div className="transfer-request-container">
                    <h3>📤 Incoming File Transfer</h3>
                    <div className="transfer-request-details">
                        <p><strong>{transferRequest.fileCount}</strong> file(s) • <strong>{formatFileSize(transferRequest.totalSize)}</strong></p>
                        <div className="file-preview">
                            {transferRequest.files.slice(0, 5).map((file, index) => (
                                <div key={index} className="file-preview-item">
                                    <span>{file.name}</span>
                                    <span>({formatFileSize(file.size)})</span>
                                </div>
                            ))}
                            {transferRequest.files.length > 5 && (
                                <div className="file-preview-item">
                                    <span>... and {transferRequest.files.length - 5} more files</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="transfer-request-actions">
                        <button className="btn btn-success" onClick={acceptTransfer}>✅ Accept Transfer</button>
                        <button className="btn btn-danger" onClick={declineTransfer}>❌ Decline</button>
                    </div>
                    <p className="timeout-notice">⏰ This request will expire in 30 seconds</p>
                </div>
            )}
            
            {transferState === 'transferring' && (
                <div className="file-transfer-list">
                    <h4>Receiving Files:</h4>
                    {Array.from(receivingFiles.values()).map((file, index) => (
                        <div key={file.id} className="file-transfer-item transferring">
                            <div className="file-info">
                                <span className="file-name">{index + 1}. {file.name}</span>
                                <span className="file-size">({formatFileSize(file.size)})</span>
                            </div>
                            <div className="transfer-details">
                                <progress className="progress-bar file" value={file.progress} max="100"></progress>
                                <div className="transfer-stats">
                                    <span>{file.progress}%</span>
                                    <span>{formatFileSize(file.receivedBytes)} / {formatFileSize(file.size)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {transferState === 'completed' && (
                <div className="received-files-container">
                    <h3>📥 All Files Received</h3>
                    <div className="received-files-list">
                        {Array.from(receivingFiles.values()).map((file, index) => (
                            <div key={file.id} className="received-file-item">
                                <div className="file-info">
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">({formatFileSize(file.size)})</span>
                                </div>
                                <a href={file.url} download={file.name} className="btn btn-small">💾 Download</a>
                            </div>
                        ))}
                    </div>
                    <button className="btn btn-primary" onClick={downloadAllFiles}>📦 Download All & Reset</button>
                </div>
            )}

            {peerId && transferState === 'waiting' && (
                <div className="qr-container">
                    <div className="status-container">
                        <div className={`status-dot ${connectionStatus}`} />
                        <p>
                            {connectionStatus === 'connecting' && 'Connecting...'}
                            {connectionStatus === 'connected' && 'Sender Connected'}
                            {connectionStatus === 'disconnected' && 'Waiting for sender...'}
                        </p>
                    </div>
                    <QRCodeCanvas value={peerId} size={256} />
                    <p>Scan this QR code or enter manually: <strong>{peerId}</strong></p>
                </div>
            )}
            {!peerId && <p>Generating ID...</p>}
        </div>
    );
};

export default Receiver;
